import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router";
import { UserService } from "@services";
import utils from "@utils";
import { USER_VETTED_STATUS, USER_TYPE } from "@constants";
import {
	EditAccountModal,
	ImpersonateModal,
	SearchField,
	ReactSelect,
	ScrollDetect,
} from "../../../components";

import "./UserList.scss";

const USER_TYPE_LABELS = [
	{ value: USER_TYPE.CLIENT, label: "Client" },
	{ value: USER_TYPE.AGENCY_ADMIN, label: "Agency" },
	{ value: USER_TYPE.LAW_ENFORCEMENT, label: "LEA" },
	{ value: USER_TYPE.COORDINATOR, label: "Coordinator" },
];

const UserList = ({ router, user }) => {
	const {
		location: { query },
	} = router;
	const { lang } = global;
	const displayCount = 25;
	const [users, setUsers] = useState([]);
	const [hasMore, setHasMore] = useState(true);
	const [pageNumber, setPageNumber] = useState(1);
	const [visible, setVisible] = useState(false);
	const [searchTerm, setSearchTerm] = useState(query.searchTerm || "");
	const [filters, setFilters] = useState({
		vettedStatus: parseInt(query.vettedStatus, 10) || "",
		userType: parseInt(query.userType, 10) || "",
	});
	const [impersonate, setImpersonate] = useState(false);
	const [userId, setUserId] = useState("");

	const _userModal = useRef(null);

	/**
	 * Gets the list of all the users
	 */
	const getUsers = useCallback((resetPageNumber = false) => {
		const search = {
			EmailAddress: searchTerm,
			FirstName: searchTerm,
			LastName: searchTerm,
			MobilePhone: searchTerm,
			Zipcode: searchTerm,
		};
		const newPageNumber = resetPageNumber ? 1 : pageNumber;
		/** Remove empty query parameters for the API request */
		const queryParams = Object.fromEntries(
			Object.entries(filters).filter(item => item[1] !== ""),
		);
		UserService.searchUsers({ ...queryParams, pageNumber: newPageNumber, displayCount }, search)
			.then(({ data }) => {
				const newUsers = data.ItemList ?? [];
				setUsers(newPageNumber > 1 ? [...users, ...newUsers] : newUsers);
				setPageNumber(newPageNumber + 1);
				setHasMore(newUsers.length >= displayCount);
			})
			.catch(({ response: { data } }) => {
				setUsers([]);
				setPageNumber(1);
				setHasMore(false);
				utils.notify({
					type: "error",
					message: data?.Message ?? lang.t("Error loading Users"),
					title: "Error",
				});
			});
	});

	/**
	 * updates the filter value in the state everytime a new value is received
	 * @param {String} property could be userType or vettedStatus and used to set the appropriate state value depending on the selection
	 * @param {Event} event an Event used to receive the user selections.
	 */
	const onFilter = property => event => {
		if (event?.value !== filters[property]) {
			setFilters(prevFilters => ({ ...prevFilters, [property]: event?.value ?? "" }));
		}
	};

	/**
	 * Handles Row click to open the edit Account modal.
	 * @param {Number} id A unique Id for every user.
	 */
	const onRowClick = id => {
		_userModal.current.open(id);
	};

	/**
	 * Opens the Impersonate Modal by providing it with the user Id it needs to impersonate.
	 * @param {Event} e The Event is used to call the stopPropagation method which is used to prevent further propagation of the current event.
	 * @param {Number} id The user Id provided to the modal in order to impersonate that user.
	 */
	const openImpersonateModal = (e, { id }) => {
		e.stopPropagation();
		setUserId(id);
		setImpersonate(true);
	};

	/** Reset visibility of the scrolldetect element after it has become visible once */
	useEffect(() => {
		if (visible) {
			getUsers();
			setVisible(false);
		}
	}, [visible]);

	/** Whenever a filter property is changed, retrieve new data from the API by clearing old data and making the ScrolLDetect visible
	 *  This automatically calls getProjects
	 */
	useEffect(() => {
		const params = Object.fromEntries(
			Object.entries(filters).filter(([, value]) => value !== ""),
		);
		if (searchTerm) params.searchTerm = searchTerm;
		router.push({ pathname: "/users", query: params });
		setPageNumber(1);
		setUsers([]);
		setHasMore(true);
	}, [filters.vettedStatus, filters.userType, searchTerm]);

	return (
		<div
			className="container"
			id="users-list"
		>
			<div className="row">
				<h2>Users</h2>
			</div>
			<div className="filters row">
				<div className="col-sm-4">
					<div className="form-group">
						<label htmlFor="searchUser">{`${lang.t("Search")} ${lang.t("Users")}`}</label>
						<SearchField
							id="searchUser"
							placeholder="Search by Email, ID or Name"
							defaultValue={searchTerm}
							clearable
							onSearch={value => setSearchTerm(value)}
						/>
					</div>
				</div>
				<div className="col-sm-4">
					<div className="form-group">
						<label htmlFor="vetted">{lang.t("Vetted Status")}</label>
						<ReactSelect
							id="vetted"
							value={filters.vettedStatus}
							name="vetted"
							onChange={onFilter("vettedStatus")}
							options={USER_VETTED_STATUS}
							isClearable
						/>
					</div>
				</div>
				<div className="col-sm-4">
					<div className="form-group">
						<label htmlFor="userType">{lang.t("User Type")}</label>
						<ReactSelect
							id="userType"
							value={filters.userType}
							name="userType"
							onChange={onFilter("userType")}
							options={USER_TYPE_LABELS}
							isClearable
						/>
					</div>
				</div>
			</div>
			<div className="row hidden-xs table-headers">
				<div className="col-sm-1">
					<strong>{lang.t("ID")}</strong>
				</div>
				<div className="col-sm-2">
					<strong>{lang.t("User")}</strong>
				</div>
				<div className="col-sm-2">
					<strong>{lang.t("User Type")}</strong>
				</div>
				<div className="col-sm-3">
					<strong>{lang.t("EMail")}</strong>
				</div>
				<div className="col-sm-2">
					<strong>{lang.t("Organization")}</strong>
				</div>
				<div className="col-sm-2">
					<strong>{lang.t("Created")}</strong>
				</div>
			</div>
			<div className="table-body">
				<For
					each="user"
					of={users}
				>
					<div
						key={`user_${user.Id}`}
						role="button"
						className="row"
						tabIndex="0"
						onClick={() => onRowClick(user.Id)}
						onKeyPress={({ key }) => {
							if (key === "Enter") onRowClick(user.Id);
						}}
					>
						<div className="col-sm-1 col-xs-6 col-xs-push-6 col-sm-push-0 text-right">
							<button
								type="button"
								className="btn btn-link"
								onClick={e => openImpersonateModal(e, { id: user.Id })}
							>
								{user.Id}
							</button>
						</div>
						<div className="col-sm-2 col-xs-6 col-xs-pull-6 col-sm-pull-0">
							{user.FirstName} {user.LastName}
						</div>
						<div className="col-xs-12 col-sm-2">
							{USER_TYPE_LABELS.find(({ value }) => value === user.UserTypeId)?.label}
						</div>
						<div
							className="col-sm-3 truncate"
							title={user.EmailAddress}
						>
							<a
								href={`mailto:${user.EmailAddress}`}
								onClick={e => e.stopPropagation()}
							>
								{user.EmailAddress}
							</a>
						</div>
						<div className="col-sm-2">
							<Link
								to={`/organizations/${user.OrganizationId}`}
								onClick={e => e.stopPropagation()}
							>
								{user.OrganizationName}
							</Link>
							<br />
							<small>
								{user.City}
								{user.City && user.State ? ", " : ""}
								{user.State}
							</small>
						</div>
						<div className="col-sm-2">
							<span className="visible-sm-inline visible-xs-inline">{`${lang.t("Created")} `}</span>
							{new Date(`${user.CreateDateUTC}Z`).toLocaleString("en-US", {
								dateStyle: "short",
								timeStyle: "short",
							})}
							<br />
							<Choose>
								<When condition={user.VettedStatus === 1}>
									<span className="label label-warning">{lang.t("Pending")}</span>
								</When>
								<When condition={user.VettedStatus === 2}>
									<span className="label label-success">
										{lang.t("Approved")}
									</span>
								</When>
								<When condition={user.VettedStatus === 3}>
									<span className="label label-danger">{lang.t("Denied")}</span>
								</When>
							</Choose>
						</div>
					</div>
				</For>
				<If condition={hasMore}>
					<ScrollDetect
						className="text-center"
						onVisible={() => setVisible(true)}
					>
						<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
					</ScrollDetect>
				</If>
			</div>
			<EditAccountModal
				accountType="user"
				user={user}
				ref={_userModal}
				onFinished={() => getUsers(true)}
			/>
			<ImpersonateModal
				userIdFromList={userId}
				show={impersonate}
				toggleModal={() => setImpersonate(!impersonate)}
			/>
		</div>
	);
};

UserList.propTypes = {
	router: PropTypes.object.isRequired,
	user: PropTypes.object,
};

UserList.defaultProps = {
	user: {},
};

export default UserList;
